/**
 * 依存ライブラリ無しの薄い IndexedDB ラッパ。
 *
 * idb 等を入れてもよいが、ROM 保存に必要な操作は限られているのと、
 * PWA の初回ロードを軽くしたいので自前で持つ。
 */

/**
 * IndexedDB 名。旧称由来の固定 ID。
 * 変更すると既存端末の ROM・セーブが消えるため、ブランド改名後も据え置き。
 */
export const DB_NAME = 'retrocade';
export const DB_VERSION = 5;

export const STORE = {
  roms: 'roms',
  romData: 'romData',
  /** CUE+BIN など大容量を 1 ファイルずつ持つ（メモリ圧迫回避） */
  romParts: 'romParts',
  saves: 'saves',
  states: 'states',
  bios: 'bios',
  settings: 'settings',
  conflicts: 'conflicts',
  cheats: 'cheats',
} as const;

export type StoreName = (typeof STORE)[keyof typeof STORE];

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = req.result;
      const oldVersion = event.oldVersion;

      if (oldVersion < 1) {
        // メタデータ（一覧表示で使う軽いレコード）
        const roms = db.createObjectStore(STORE.roms, { keyPath: 'id' });
        roms.createIndex('system', 'system');
        roms.createIndex('addedAt', 'addedAt');
        roms.createIndex('lastPlayedAt', 'lastPlayedAt');
        roms.createIndex('sha1', 'sha1', { unique: false });

        // ROM 本体は別ストアに分ける。
        // 一覧取得のたびに数十 MB の ArrayBuffer を読み込むのを避けるため。
        db.createObjectStore(STORE.romData, { keyPath: 'id' });

        const saves = db.createObjectStore(STORE.saves, { keyPath: 'romId' });
        saves.createIndex('updatedAt', 'updatedAt');

        const states = db.createObjectStore(STORE.states, { keyPath: 'id' });
        states.createIndex('romId', 'romId');
        states.createIndex('createdAt', 'createdAt');

        db.createObjectStore(STORE.bios, { keyPath: 'id' });
        db.createObjectStore(STORE.settings, { keyPath: 'key' });
      }

      if (oldVersion < 2) {
        // v2: サーバー同期用の同期状態インデックスを追加
        const tx = req.transaction!;
        const saves = tx.objectStore(STORE.saves);
        if (!saves.indexNames.contains('syncState')) {
          saves.createIndex('syncState', 'syncState');
        }
      }

      if (oldVersion < 3) {
        // v3: 双方向同期。競合を保留するストアと、
        //     セーブステート側の同期状態インデックスを追加する。
        const tx = req.transaction!;

        const states = tx.objectStore(STORE.states);
        if (!states.indexNames.contains('syncState')) {
          states.createIndex('syncState', 'syncState');
        }

        if (!db.objectStoreNames.contains(STORE.conflicts)) {
          const conflicts = db.createObjectStore(STORE.conflicts, { keyPath: 'id' });
          conflicts.createIndex('romId', 'romId');
          conflicts.createIndex('detectedAt', 'detectedAt');
        }
      }

      if (oldVersion < 4) {
        // v4: チートコード
        if (!db.objectStoreNames.contains(STORE.cheats)) {
          const cheats = db.createObjectStore(STORE.cheats, { keyPath: 'id' });
          cheats.createIndex('romId', 'romId');
        }
      }

      if (oldVersion < 5) {
        // v5: ディスクイメージを分割保存（PS1 の巨大 BIN でタブ落ちしないように）
        if (!db.objectStoreNames.contains(STORE.romParts)) {
          const parts = db.createObjectStore(STORE.romParts, { keyPath: 'id' });
          parts.createIndex('romId', 'romId');
        }
      }
    };

    req.onsuccess = () => {
      const db = req.result;
      // 別タブがバージョンアップを要求したら道を譲る
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };

    req.onerror = () => reject(req.error ?? new Error('failed to open IndexedDB'));
    req.onblocked = () =>
      reject(new Error('IndexedDB がほかのタブでロックされています。他のタブを閉じてください。'));
  });

  return dbPromise;
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function tx<T>(
  stores: StoreName | StoreName[],
  mode: IDBTransactionMode,
  fn: (t: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  const transaction = db.transaction(stores, mode);

  const done = new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error ?? new Error('transaction aborted'));
  });

  const result = await fn(transaction);
  await done;
  return result;
}

export async function put<T>(store: StoreName, value: T): Promise<void> {
  await tx(store, 'readwrite', (t) => promisify(t.objectStore(store).put(value)));
}

export async function get<T>(store: StoreName, key: IDBValidKey): Promise<T | undefined> {
  return tx(store, 'readonly', (t) => promisify<T | undefined>(t.objectStore(store).get(key)));
}

export async function getAll<T>(store: StoreName): Promise<T[]> {
  return tx(store, 'readonly', (t) => promisify<T[]>(t.objectStore(store).getAll()));
}

export async function getAllByIndex<T>(
  store: StoreName,
  index: string,
  query: IDBValidKey | IDBKeyRange,
): Promise<T[]> {
  return tx(store, 'readonly', (t) => promisify<T[]>(t.objectStore(store).index(index).getAll(query)));
}

/** インデックスに一致するキーだけを取る（巨大 value を一度に載せない）。 */
export async function getAllKeysByIndex(
  store: StoreName,
  index: string,
  query: IDBValidKey | IDBKeyRange,
): Promise<IDBValidKey[]> {
  return tx(store, 'readonly', (t) =>
    promisify<IDBValidKey[]>(t.objectStore(store).index(index).getAllKeys(query)),
  );
}

export async function del(store: StoreName, key: IDBValidKey): Promise<void> {
  await tx(store, 'readwrite', (t) => promisify(t.objectStore(store).delete(key)));
}

export async function clear(store: StoreName): Promise<void> {
  await tx(store, 'readwrite', (t) => promisify(t.objectStore(store).clear()));
}

/* -------------------------------------------------------------------------- */
/* ストレージ容量                                                              */
/* -------------------------------------------------------------------------- */

export interface StorageEstimate {
  usage: number;
  quota: number;
  persisted: boolean;
}

/**
 * iOS Safari は 7 日間未使用の PWA でないサイトのストレージを消す。
 * ホーム画面に追加された PWA は対象外だが、念のため永続化を要求しておく。
 */
export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  if (await navigator.storage.persisted()) return true;
  return navigator.storage.persist();
}

export async function estimateStorage(): Promise<StorageEstimate> {
  const persisted = (await navigator.storage?.persisted?.()) ?? false;
  const est = (await navigator.storage?.estimate?.()) ?? {};
  return {
    usage: est.usage ?? 0,
    quota: est.quota ?? 0,
    persisted,
  };
}

/* -------------------------------------------------------------------------- */
/* ハッシュ                                                                    */
/* -------------------------------------------------------------------------- */

/** ROM の同一性判定に使う SHA-1。数十 MB でも WebCrypto なら十分速い。 */
export async function sha1(data: ArrayBuffer): Promise<string> {
  // Node/jsdom では File.arrayBuffer() の戻り値が SubtleCrypto に拒否されることがあるため、
  // 必ず自前の Uint8Array にコピーしてから渡す。
  const bytes = new Uint8Array(data.byteLength);
  bytes.set(new Uint8Array(data));
  const digest = await crypto.subtle.digest('SHA-1', bytes);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
